import { OnboardingStepType, OrganizationDetailFragment } from '@alice-financial/alice-sdk'
import { Box, Grid, Tab, Tabs, Typography } from '@alice-financial/pretext-ui'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked'
import RemoveCircleOutlineOutlinedIcon from '@mui/icons-material/RemoveCircleOutlineOutlined'
import * as React from 'react'
import { getStepStatus } from './getStepStatus'
import { OnboardingStepConfig } from './stepConfiguration'

/**
 * Get status icon based on step status
 */
const getStatusIcon = (stepStatus: { completed: boolean; error: boolean; inProgress: boolean }) => {
  if (stepStatus.completed) {
    return <CheckCircleIcon color="primary" />
  }
  if (stepStatus.inProgress) {
    return <RemoveCircleOutlineOutlinedIcon color="primary" />
  }
  // Not started or blocked - grey circle
  return <RadioButtonUncheckedIcon color="disabled" />
}

export interface OnboardingStepTabsProps {
  stepConfigs: OnboardingStepConfig[]
  currentStepType: OnboardingStepType | null
  org: OrganizationDetailFragment | null | undefined
  onStepClick: (stepType: OnboardingStepType) => void
}

/**
 * Vertical tabs navigation for onboarding steps.
 * Displays step number, title, and status icon for each step.
 * When org is null/undefined, all tabs are disabled and not clickable.
 */
export const OnboardingStepTabs: React.FC<OnboardingStepTabsProps> = ({
  stepConfigs,
  currentStepType,
  org,
  onStepClick,
}) => {
  // Find active tab index
  const activeTabIndex = currentStepType
    ? stepConfigs.findIndex((config) => config.stepType === currentStepType)
    : 0

  const isDisabled = !org

  return (
    <Tabs
      orientation="vertical"
      variant="scrollable"
      value={activeTabIndex}
      onChange={(_, newValue) => {
        if (isDisabled) return
        const targetStepType = stepConfigs[newValue]?.stepType
        if (targetStepType) {
          onStepClick(targetStepType)
        }
      }}
      sx={{
        opacity: isDisabled ? 0.6 : 1,
      }}
    >
      {stepConfigs.map(({ stepType, stepTitle }, index) => {
        const stepStatus = org
          ? getStepStatus(stepType, org)
          : { completed: false, error: false, inProgress: false }
        const isActive = stepType === currentStepType && !isDisabled
        const stepNumber = index + 1

        return (
          <Tab
            key={stepType}
            disabled={isDisabled}
            label={
              <Grid container alignItems="center" width="100%" spacing={1}>
                {/* Step Number */}
                <Box
                  sx={{
                    minWidth: '1.5rem',
                    height: '1.5rem',
                    borderRadius: '50%',
                    backgroundColor: isActive ? 'primary.main' : 'grey.300',
                    color: isActive ? 'white' : 'text.secondary',
                    border: '1px solid rgba(0,0,0,0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.75rem',
                    fontWeight: 'bold',
                  }}
                >
                  {stepNumber}
                </Box>

                {/* Step Title */}
                <Typography
                  variant="body2"
                  sx={{
                    fontVariant: 'small-caps',
                    fontWeight: isActive ? 800 : 600,
                    fontSize: '0.875rem',
                    color: stepStatus.error
                      ? 'text.disabled'
                      : stepStatus.completed
                        ? 'primary.main'
                        : 'inherit',
                    flex: 1,
                    textAlign: 'left',
                  }}
                >
                  {stepTitle}
                </Typography>

                {/* Status Icon */}
                <Box sx={{ minWidth: '1rem' }}>{getStatusIcon(stepStatus)}</Box>
              </Grid>
            }
            sx={(theme) => ({
              alignItems: 'flex-start',
              padding: theme.spacing(1.5),
              minHeight: '3rem',
              '&.Mui-selected': {
                backgroundColor: 'action.selected',
              },
            })}
          />
        )
      })}
    </Tabs>
  )
}
